import React, { createContext, useContext, useEffect, useState } from 'react'

import { useTonPlans } from '@/contexts/plans'
import cn from '@/utils/cn'

import ButtonTab from '../ButtonTab'
import CalculatorMachine from '../CalculatorMachines'
import CalculatorTapTon from '../CalculatorTapTon'

export enum AllTypeTabs {
  AllMachines = 'machines',
  Tapton = 'tapton',
  PaymentLink = 'paymentLink',
  Mei = 'mei',
  DefaultPlanPage = 'defaultPlanPage'
}

export type pageModeProps =
  | AllTypeTabs.AllMachines
  | AllTypeTabs.Tapton
  | AllTypeTabs.PaymentLink
  | AllTypeTabs.Mei
  | AllTypeTabs.DefaultPlanPage

type ButtonTabOptionsProps = {
  [key in AllTypeTabs]: {
    label: string
  }
}

export const buttonTab: ButtonTabOptionsProps = {
  [AllTypeTabs.AllMachines]: {
    label: 'Maquininha'
  },
  [AllTypeTabs.Tapton]: {
    label: 'TapTon'
  },
  [AllTypeTabs.Mei]: {
    label: 'Plano Ton pro MEI e PJ'
  },
  [AllTypeTabs.PaymentLink]: {
    label: ''
  },
  [AllTypeTabs.DefaultPlanPage]: {
    label: ''
  }
}
export interface CalculatorTabsProps {
  defaultSelected: pageModeProps
}

// Taxas de concorrentes para cálculo de economia
export const feesCompetitorsPrices = [
  3.99, 6.99, 11.91, 13.29, 14.64, 15.97, 17.27, 18.55, 19.81, 21.04, 22.24,
  23.43, 24.59
]

const SelectModeContext = createContext<{
  selectMode: pageModeProps
  setSelectMode: React.Dispatch<React.SetStateAction<pageModeProps>>
} | null>(null)

export const SelectModeProvider = ({
  children,
  defaultSelected
}: {
  children: React.ReactNode
  defaultSelected: pageModeProps
}) => {
  const [selectMode, setSelectMode] = useState<pageModeProps>(defaultSelected)

  return (
    <SelectModeContext.Provider value={{ selectMode, setSelectMode }}>
      {children}
    </SelectModeContext.Provider>
  )
}

export const useSelectMode = () => {
  const context = useContext(SelectModeContext)
  if (!context) {
    throw new Error('useSelectMode must be used within a SelectModeProvider')
  }
  return context
}

const CalculatorTabs = ({ defaultSelected }: CalculatorTabsProps) => {
  const { currentPlan } = useTonPlans()
  const { selectMode, setSelectMode } = useSelectMode()

  useEffect(() => {
    if (currentPlan === null) return

    if (defaultSelected === AllTypeTabs.Mei) {
      setSelectMode(AllTypeTabs.Mei)
      return
    }

    if (defaultSelected === AllTypeTabs.DefaultPlanPage) {
      setSelectMode(AllTypeTabs.DefaultPlanPage)
      return
    }

    if (currentPlan.type === 'transactional') {
      setSelectMode(AllTypeTabs.AllMachines)
      return
    }

    if (currentPlan.type === 'tap_phone') {
      setSelectMode(AllTypeTabs.Tapton)
      return
    }
  }, [currentPlan, defaultSelected, setSelectMode])

  return (
    <div className="flex flex-col w-full md:w-auto">
      <div className="overflow-x-auto w-full">
        <div className="flex flex-nowrap gap-8">
          <ButtonTab
            id="btn-newcalc-maquininha"
            option={AllTypeTabs.AllMachines}
            selectMode={selectMode}
            setSelectMode={setSelectMode}
            className={cn(
              selectMode === AllTypeTabs.Mei && 'hidden',
              selectMode === AllTypeTabs.DefaultPlanPage && 'hidden'
            )}
          >
            {buttonTab[AllTypeTabs.AllMachines].label}
          </ButtonTab>
          <ButtonTab
            id="btn-newcalc-tapton"
            option={AllTypeTabs.Tapton}
            selectMode={selectMode}
            setSelectMode={setSelectMode}
            className={cn(
              selectMode === AllTypeTabs.Mei && 'hidden',
              selectMode === AllTypeTabs.DefaultPlanPage && 'hidden'
            )}
          >
            {buttonTab[AllTypeTabs.Tapton].label}
          </ButtonTab>
          <ButtonTab
            id="btn-newcalc-mei"
            option={AllTypeTabs.Mei}
            selectMode={selectMode}
            setSelectMode={setSelectMode}
            className={cn(
              selectMode !== AllTypeTabs.Mei && 'hidden',
              selectMode === AllTypeTabs.DefaultPlanPage && 'hidden'
            )}
          >
            {buttonTab[AllTypeTabs.Mei].label}
          </ButtonTab>
          <ButtonTab
            id={`btn-newcalc-${currentPlan?.commercial_name}`}
            option={AllTypeTabs.DefaultPlanPage}
            selectMode={selectMode}
            setSelectMode={setSelectMode}
            className={cn(
              selectMode !== AllTypeTabs.DefaultPlanPage && 'hidden'
            )}
          >
            {currentPlan?.commercial_name}
          </ButtonTab>
        </div>
      </div>
      {(selectMode === AllTypeTabs.AllMachines ||
        selectMode === AllTypeTabs.Mei ||
        selectMode === AllTypeTabs.DefaultPlanPage) && (
        <CalculatorMachine selectMode={selectMode} />
      )}
      {selectMode === AllTypeTabs.Tapton && <CalculatorTapTon />}
    </div>
  )
}

export default CalculatorTabs
